// ─────────────────────────────────────────────────────────────────────────────
//  platform/errors.js — Frozen error catalogue (Amendment A9)
//
//  A fixed, closed set of error codes. Frontend and services key their behaviour
//  off `code`, NEVER off free-text `message`. Do not add codes outside the frozen
//  specification without a Change Request.
// ─────────────────────────────────────────────────────────────────────────────

// The canonical error catalogue — the single source of truth for error codes.
// Documented in docs/ERROR_CATALOGUE.md. This SUPERSEDES the earlier spec drafts
// that referenced ERR_CONFLICT, ERR_IN_PROGRESS and a broad ERR_IDEMPOTENCY:
//   • concurrency conflict            -> ERR_STALE_VERSION
//   • idempotency key already pending -> ERR_IDEMPOTENCY_IN_PROGRESS
//   • replay of a failed command      -> ERR_IDEMPOTENCY_REPLAY_FAILED
export const ERRORS = Object.freeze({
  ERR_PERMISSION:                 { code: 'ERR_PERMISSION',                 httpStatus: 403, message: 'You do not have permission to perform this action.' },
  ERR_STALE_VERSION:              { code: 'ERR_STALE_VERSION',              httpStatus: 409, message: 'This record was changed by someone else. Reload and try again.' },
  ERR_DUPLICATE_INVOICE:          { code: 'ERR_DUPLICATE_INVOICE',          httpStatus: 409, message: 'That invoice number already exists.' },
  ERR_OUT_OF_STOCK:               { code: 'ERR_OUT_OF_STOCK',               httpStatus: 422, message: 'There is not enough stock to complete this operation.' },
  ERR_INVALID_RETURN:             { code: 'ERR_INVALID_RETURN',             httpStatus: 422, message: 'This return is not valid.' },
  ERR_IDEMPOTENCY_IN_PROGRESS:    { code: 'ERR_IDEMPOTENCY_IN_PROGRESS',    httpStatus: 409, message: 'This request is already being processed.' },
  ERR_IDEMPOTENCY_REPLAY_FAILED:  { code: 'ERR_IDEMPOTENCY_REPLAY_FAILED',  httpStatus: 409, message: 'A previous attempt for this request did not complete. Start a new request.' },
  ERR_SUBSCRIPTION:               { code: 'ERR_SUBSCRIPTION',               httpStatus: 402, message: 'An active subscription is required for this action.' },
  ERR_VALIDATION:                 { code: 'ERR_VALIDATION',                 httpStatus: 400, message: 'The submitted data is invalid.' },
  ERR_INTERNAL:                   { code: 'ERR_INTERNAL',                   httpStatus: 500, message: 'An unexpected error occurred.' },
});

// The frozen list of codes, for validation and exhaustive frontend handling.
export const ERROR_CODES = Object.freeze(Object.keys(ERRORS));

// Resolve a spec from either a code string ('ERR_PERMISSION') or a spec object.
function resolveSpec(spec) {
  if (spec && typeof spec === 'object' && spec.code && ERRORS[spec.code]) return ERRORS[spec.code];
  if (typeof spec === 'string' && ERRORS[spec]) return ERRORS[spec];
  throw new Error(`Unknown error code: ${String(spec && spec.code ? spec.code : spec)}`);
}

// A typed application error carrying a catalogue code, HTTP status and optional
// structured details. Thrown by services/commands; converted to the response
// envelope at the boundary (see platform/response.js).
export class AppError extends Error {
  constructor(spec, message, details) {
    const s = resolveSpec(spec);
    super(message || s.message);
    this.name = 'AppError';
    this.code = s.code;
    this.httpStatus = s.httpStatus;
    this.details = details == null ? null : details;
  }
}

export const isAppError = (e) => e instanceof AppError;

// Convenience: throw a catalogue error in one call.
export const raise = (code, message, details) => { throw new AppError(code, message, details); };
